import type { FakeBlock } from "./responses";

// Canned AI-chat conversations for the HCIC booth demo (Kylie's
// "Freshpaint AI - HCIC Booth Demo" deck). Scoped to the HCIC demo env via
// isHcicDemoEnv; see hcic-demo.ts. Every visual Kylie specced maps to an
// existing chat block kind (bar/line chart, table, or KPI cards), so no new
// renderer is needed. Prose is Kylie's copy, lightly formatted as Markdown.
//
// A few responses reference the standalone Recommendation Cards experience and
// link to it with [View Recommendation Cards](/attribution), matching how the
// real agent handles rec-card questions. The /attribution page is given canned
// cards in preview mode for this same env (see handlers/insights.ts).

// ---------------------------------------------------------------------------
// Canonical prompt strings — reused as RESPONSES_BY_PROMPT keys, splash
// prompts, fallback suggestions, and seeded sidebar threads so every launch
// point matches a key exactly.
// ---------------------------------------------------------------------------

export const HCIC_PROMPTS = {
  topPaidChannel: "What's my top paid channel?",
  googleConverting: "How well is my Google Ads traffic converting?",
  improvePaid: "What should I do to improve my paid marketing?",
  optingOut: "Are more people opting out of tracking?",
  exitPages: "Where are people leaving my site before they convert?",
  adEfficiency: "How can I improve my ad efficiency?",
  formFillsDrop: "Why did form fills drop this week?",
  abandonPages: "Which pages are patients abandoning?",
  platformsMismatch:
    "My ad platforms and attribution don't match. Which channel is actually driving conversions?",
  consentIssue:
    "Did a consent or tracking issue affect my Meta or Google numbers?",
  appointmentsDrop:
    "Why did appointment requests drop, and where should I start looking?",
} as const;

// Shown on the empty-chat splash for the HCIC env (see ChatArea), and used as
// the fallback disclaimer's suggestions so a non-canned question still points
// back at prompts that work.
export const HCIC_SPLASH_PROMPTS: string[] = [
  HCIC_PROMPTS.topPaidChannel,
  HCIC_PROMPTS.googleConverting,
  HCIC_PROMPTS.improvePaid,
  HCIC_PROMPTS.optingOut,
  HCIC_PROMPTS.exitPages,
  HCIC_PROMPTS.formFillsDrop,
];

export const HCIC_SUGGESTED_FOLLOWUPS: string[] = [
  HCIC_PROMPTS.topPaidChannel,
  HCIC_PROMPTS.improvePaid,
  HCIC_PROMPTS.exitPages,
];

const HCIC_PREVIEW_DISCLAIMER =
  "This is preview mode, so I can't actually answer this question against your real data. " +
  "Exit preview mode (top of the page) to send real questions to Freshpaint AI. " +
  "In the meantime, try one of the suggestions below — they have canned example responses you can explore.";

// ---------------------------------------------------------------------------
// Visuals (data straight from the deck's per-visual sheets; example
// screenshots only informed the block kind and framing)
// ---------------------------------------------------------------------------

const VISUAL_1_CHART: FakeBlock = {
  kind: "chart",
  data: {
    chartType: "bar",
    title: "Bottom-funnel conversion credit by paid channel",
    subtitle: "Google Ads leads every paid channel on last-touch credit.",
    dataSource:
      "Attributed conversions by paid channel, last-touch model, Jul 15 – Oct 12, 2026",
    xAxisLabel: "Channel",
    yAxisLabel: "Conversion credit",
    series: [
      {
        name: "Conversion credit",
        data: [
          { label: "Google Ads", value: 412 },
          { label: "Meta Ads", value: 168 },
          { label: "Bing Ads", value: 94 },
          { label: "CM360", value: 38 },
          { label: "LinkedIn Ads", value: 21 },
          { label: "Reddit Ads", value: 9 },
        ],
      },
    ],
  },
};

const VISUAL_2_TABLE: FakeBlock = {
  kind: "table",
  data: {
    title: "Paid channel summary — last touch, Jul 15 – Oct 12, 2026",
    columns: [
      { key: "channel", label: "Channel" },
      { key: "bottom", label: "Bottom-funnel credit" },
      { key: "mid", label: "Mid-funnel credit" },
      { key: "spend", label: "Spend" },
      { key: "cpa", label: "CPA" },
      { key: "dates", label: "Active dates" },
    ],
    rows: [
      ["Google Ads", 412, 1186, "$48,600", "$117.96", "Jul 15 – Oct 12"],
      ["Meta Ads", 168, 902, "$22,400", "$133.33", "Jul 15 – Oct 12"],
      ["Bing Ads", 94, 211, "$8,350", "$88.83", "Jul 15 – Oct 12"],
      ["CM360", 38, 240, "$9,500", "$250.00", "Jul 15 – Oct 12"],
      ["LinkedIn Ads", 21, 64, "$6,900", "$328.57", "Jul 15 – Oct 12"],
      ["Reddit Ads", 9, 37, "$1,800", "$200.00", "Aug 1 – Oct 12"],
    ],
  },
};

const VISUAL_3_TABLE: FakeBlock = {
  kind: "table",
  data: {
    title: "Channel roles in conversion paths (30-day summary)",
    columns: [
      { key: "channel", label: "Channel" },
      { key: "role", label: "Role" },
      { key: "touched", label: "Conversions touched" },
      { key: "first", label: "First touch %" },
      { key: "last", label: "Last touch %" },
      { key: "multi", label: "Multi-channel %" },
    ],
    rows: [
      ["Direct traffic", "Closer", 342, "12%", "71%", "58%"],
      ["Organic search", "Assist", 210, "41%", "18%", "79%"],
      ["Google Ads", "Closer", 186, "38%", "61%", "72%"],
      ["Meta Ads", "Opener", 124, "64%", "22%", "85%"],
      ["CM360", "Assist", 47, "21%", "6%", "94%"],
      ["Bing Ads", "Closer", 31, "29%", "58%", "52%"],
    ],
  },
};

const VISUAL_4_KPIS: FakeBlock = {
  kind: "kpis",
  data: {
    title: "Google Ads efficiency — last 30 days",
    items: [
      {
        label: "Bottom-funnel conversions",
        value: "113",
        trend: null,
        trendDir: "neutral",
        sub: "Last-touch attributed",
      },
      {
        label: "Conversions per touch",
        value: "4.59%",
        trend: null,
        trendDir: "neutral",
        sub: "Clicks only (0 impression touches)",
      },
      {
        label: "Vs. Meta Ads",
        value: "0.68%",
        trend: "-3.91%",
        trendDir: "down",
        sub: "Meta Ads conversions per touch",
      },
      {
        label: "Vs. organic search",
        value: "4.42%",
        trend: null,
        trendDir: "neutral",
        sub: "Effectively tied with Google Ads",
      },
    ],
  },
};

const VISUAL_5_CHART: FakeBlock = {
  kind: "chart",
  data: {
    chartType: "bar",
    title: "Bottom-funnel conversions per touch by channel",
    subtitle:
      "Last-touch attribution, last 30 days. Google Ads ties organic search for top efficiency.",
    dataSource: "Marketing attribution data, last 30 days",
    xAxisLabel: "Channel",
    yAxisLabel: "Conversions per touch (%)",
    series: [
      {
        name: "Conversions per touch",
        data: [
          { label: "Google Ads", value: 4.59 },
          { label: "Organic Search", value: 4.42 },
          { label: "Email", value: 2.61 },
          { label: "Generative AI", value: 2.14 },
          { label: "Social Media", value: 0.92 },
          { label: "Meta Ads", value: 0.68 },
          { label: "Direct", value: 0.55 },
        ],
      },
    ],
  },
};

const VISUAL_6_KPIS: FakeBlock = {
  kind: "kpis",
  data: {
    title: "Paid channel snapshot — last 30 days",
    items: [
      {
        label: "Google Ads conversions",
        value: "113",
        trend: "70% of paid total",
        trendDir: "neutral",
        sub: "$15.9K spend · $141 CPA",
      },
      {
        label: "Meta Ads conversions",
        value: "27",
        trend: "17% of paid total",
        trendDir: "neutral",
        sub: "$7.3K spend · $270 CPA (last touch)",
      },
      {
        label: "Google Ads CPA",
        value: "$141",
        trend: "+29%",
        trendDir: "down",
        sub: "vs. $109 prior 60 days",
      },
      {
        label: "Bing Ads CPA",
        value: "$86",
        trend: "Lowest paid CPA",
        trendDir: "up",
        sub: "18 conversions · $1.55K spend",
      },
    ],
  },
};

const VISUAL_7_CHART: FakeBlock = {
  kind: "chart",
  data: {
    chartType: "bar",
    title: "Paid channel attributed conversions",
    subtitle: "Google Ads leads paid volume. Bing Ads has the lowest CPA.",
    dataSource: "Marketing attribution data, last 30 days, last-touch model",
    xAxisLabel: "Channel",
    yAxisLabel: "Attributed conversions",
    series: [
      {
        name: "Attributed conversions",
        data: [
          { label: "Google Ads", value: 113 },
          { label: "Meta Ads", value: 27 },
          { label: "Bing Ads", value: 18 },
          { label: "CM360", value: 3 },
        ],
      },
    ],
  },
};

const VISUAL_8_KPIS: FakeBlock = {
  kind: "kpis",
  data: {
    title: "Consent impact snapshot",
    items: [
      {
        label: "Ad destination exclusion rate",
        value: "12.7%",
        trend: "+3.1 pts",
        trendDir: "neutral",
        sub: "Events withheld from Meta Ads and Google Ads",
      },
      {
        label: "Banner decisions (30 days)",
        value: "52,400",
        trend: "Active",
        trendDir: "neutral",
        sub: "Last recorded Oct 12, 2026",
      },
      {
        label: "Peak excluded events (Sep)",
        value: "421K",
        trend: "Out of 3.4M total",
        trendDir: "neutral",
        sub: "12.4% of events",
      },
      {
        label: "Exclusion cause",
        value: "68%",
        trend: "Declined advertising only",
        trendDir: "neutral",
        sub: "Still counted in analytics",
      },
    ],
  },
};

const VISUAL_9_CHART: FakeBlock = {
  kind: "chart",
  data: {
    chartType: "line",
    title: "Consent exclusion rate by month",
    subtitle: "Ad destinations are hit harder than analytics.",
    dataSource: "Consent exclusions by destination, Apr 1 – Oct 12, 2026",
    xAxisLabel: "Month",
    yAxisLabel: "Exclusion rate (%)",
    series: [
      {
        name: "Ad destinations",
        data: [
          { label: "Apr", value: 9.6 },
          { label: "May", value: 9.9 },
          { label: "Jun", value: 10.2 },
          { label: "Jul", value: 10.8 },
          { label: "Aug", value: 11.9 },
          { label: "Sep", value: 12.4 },
          { label: "Oct", value: 12.7 },
        ],
      },
      {
        name: "Analytics",
        data: [
          { label: "Apr", value: 3.2 },
          { label: "May", value: 3.3 },
          { label: "Jun", value: 3.4 },
          { label: "Jul", value: 3.6 },
          { label: "Aug", value: 3.9 },
          { label: "Sep", value: 4.0 },
          { label: "Oct", value: 4.1 },
        ],
      },
    ],
  },
};

const VISUAL_10_TABLE: FakeBlock = {
  kind: "table",
  data: {
    title: "Top exit pages, non-converting sessions (last 30 days)",
    columns: [
      { key: "page", label: "Page" },
      { key: "exits", label: "Non-converting exits" },
      { key: "views", label: "Total pageviews" },
      { key: "rate", label: "Exit rate" },
    ],
    rows: [
      ["/pay-bill", "18,420", "52,310", "35.2%"],
      ["/", "8,140", "17,260", "47.2%"],
      ["/pay-bill/confirmation", "5,930", "9,880", "60.0%"],
      ["/locations/", "4,215", "9,370", "45.0%"],
      ["/find-a-doctor/", "2,860", "5,140", "55.6%"],
      ["/services/", "1,390", "3,610", "38.5%"],
      ["/locations/northside-clinic/", "1,184", "1,251", "94.6%"],
      ["/virtual-care/", "1,052", "1,340", "78.5%"],
      ["/services/orthopedics/", "911", "1,830", "49.8%"],
      ["/about/", "862", "1,795", "48.0%"],
    ],
  },
};

const VISUAL_11_CHART: FakeBlock = {
  kind: "chart",
  data: {
    chartType: "bar",
    title: "Non-converting exit rate by page",
    subtitle:
      "Pages sorted by exit rate. Higher = more visitors leaving without converting.",
    dataSource:
      "Session and pageview data with conversion events, last 30 days",
    xAxisLabel: "Page",
    yAxisLabel: "Non-converting exit rate (%)",
    series: [
      {
        name: "Non-converting exit rate",
        data: [
          { label: "/locations/northside-clinic/", value: 94.6 },
          { label: "/virtual-care/", value: 78.5 },
          { label: "/pay-bill/confirmation", value: 60.0 },
          { label: "/find-a-doctor/", value: 55.6 },
          { label: "/services/orthopedics/", value: 49.8 },
          { label: "/about/", value: 48.0 },
          { label: "/", value: 47.2 },
          { label: "/locations/", value: 45.0 },
          { label: "/services/", value: 38.5 },
          { label: "/pay-bill", value: 35.2 },
        ],
      },
    ],
  },
};

const VISUAL_12_KPIS: FakeBlock = {
  kind: "kpis",
  data: {
    title: "Appointment requests this week vs. last",
    items: [
      {
        label: "Last week CVR (Mon–Thu)",
        value: "1.33%",
        trend: null,
        trendDir: "neutral",
        sub: "80 requests / 6,020 sessions",
      },
      {
        label: "This week CVR (Mon–Thu)",
        value: "1.01%",
        trend: "-0.32 pts",
        trendDir: "down",
        sub: "60 requests / 5,940 sessions",
      },
      {
        label: "Daily appointment requests",
        value: "15/day",
        trend: "-25%",
        trendDir: "down",
        sub: "vs. 20/day last week",
      },
      {
        label: "Google Ads request pace",
        value: "2.5/day",
        trend: "-47%",
        trendDir: "down",
        sub: "vs. 4.8/day last week",
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Response bodies (shared across the prompts that map to them). Each prompt
// appends its own explore-next suggestions on top of the shared body.
// ---------------------------------------------------------------------------

const BODY_TOP_PAID_CHANNEL: FakeBlock[] = [
  {
    kind: "text",
    text: "Google Ads is your top paid channel, with 412 bottom-funnel conversion credits from Jul 15 to Oct 12, 2026, under the last-touch attribution model. Meta Ads is second with 168, and Bing Ads is third with 94. Google Ads stays on top under every attribution model. Under first touch, though, Meta Ads nearly catches up (331 vs. 356), because it starts many of the journeys Google Ads finishes.",
  },
  {
    kind: "text",
    text: "Bottom-funnel conversion credit is the credit a channel gets for your bottom-of-funnel goal, here Appointment Request Submitted. Under last touch, all the credit goes to the last channel before the conversion. Cost per acquisition (CPA) is spend divided by that credit.",
  },
  VISUAL_1_CHART,
  VISUAL_2_TABLE,
  {
    kind: "text",
    text: "**Why your ad platforms and Freshpaint don't match**\n\nTogether, Google Ads, Meta Ads and Bing Ads report 989 conversions for this period. Freshpaint credits them with 674.\n\n- Google Ads reports 560, and Freshpaint credits 412\n- Meta Ads reports 311, and Freshpaint credits 168\n- Bing Ads reports 118, and Freshpaint credits 94",
  },
  {
    kind: "text",
    text: "The gap is expected:\n\n- **Each platform claims the same patient.** If someone clicks a Meta ad and later a Google ad, both platforms count the conversion. Freshpaint gives it to one channel (last touch) or splits it (multi-touch models).\n- **Platforms count view-throughs and use their own click windows.** Freshpaint only credits visits it tracked on your site.\n- **The two sides count different dates.** Freshpaint counts a conversion on the day it happened. Ad platforms usually count it on the day of the click or impression, so recent days won't line up.",
  },
  VISUAL_3_TABLE,
  {
    kind: "text",
    text: "**Caveats**\n\n- These credits are appointment requests, not booked appointments or revenue. Return on ad spend (ROAS) isn't available in attribution data.\n- LinkedIn Ads (21 credits) and Reddit Ads (9 credits) have low volume, so treat their CPAs as directional.\n- Data used: Appointment Request Submitted, last touch, Jul 15 to Oct 12, 2026. You can verify the paths in the Journeys view.",
  },
  {
    kind: "text",
    text: "**What to tackle:** Before you cut Meta Ads because of its last-touch CPA ($133.33 vs. Google Ads' $117.96), compare the two under first touch or linear. Meta Ads is opening journeys that Google Ads closes.",
  },
  {
    kind: "text",
    text: "Google Ads is the top paid channel (412 bottom-funnel credits, last touch, Jul 15 to Oct 12), followed by Meta Ads (168) and Bing Ads (94). The platforms report 989 conversions combined, compared with 674 in Freshpaint, mostly because of double counting and view-throughs. Meta Ads plays a bigger role as an opener than last touch shows.",
  },
];

const BODY_GOOGLE_CONVERTING: FakeBlock[] = [
  {
    kind: "text",
    text: "Google Ads is your most efficient paid channel, converting at 4.59% per touch over the last 30 days. That's nearly tied with organic search (4.42%) and well ahead of Meta Ads (0.68%). Google Ads drove 113 bottom-funnel conversions from 2,460 clicks, about 7× Meta's rate per touch, even though Meta Ads had about 60% more touches.",
  },
  VISUAL_4_KPIS,
  {
    kind: "text",
    text: "**Caveats on these numbers:**\n\n- They use last-touch attribution, so the credit goes to the final touchpoint before the conversion. Google Ads would likely get different credit under the linear or U-shaped model.\n- Conversions per touch isn't the same as a traditional conversion rate (conversions ÷ sessions). It's the closest ratio available for comparing channels in the attribution view.\n- Meta Ads had 3,980 touches but only 27 last-touch bottom-funnel conversions. Part of that is Meta's role: it often starts journeys that another channel finishes, which last touch doesn't credit. Before you change Meta's targeting or landing pages, compare it under first touch.\n- Data used: Appointment Request Submitted, last touch, Sep 13 to Oct 12, 2026.",
  },
  VISUAL_5_CHART,
  {
    kind: "text",
    text: "Google Ads converts at 4.59% per touch (113 conversions from 2,460 clicks, last 30 days, last touch). It's tied with organic search (4.42%) and about 7× Meta Ads (0.68%).",
  },
];

const BODY_IMPROVE_PAID: FakeBlock[] = [
  {
    kind: "text",
    text: "**Your paid marketing: 3 things to act on now**\n\nThis week's recommendation cards are all new and cover three issues across your paid channels. Here's the full picture and what to prioritize.",
  },
  VISUAL_6_KPIS,
  {
    kind: "text",
    text: "**1. Find out why Google Ads CPA is climbing**\n\nGoogle Ads is still your top paid channel, with 113 conversions (70% of paid volume) in the last 30 days. Its cost per acquisition (CPA), though, rose to $141, up 29% from $109 over the previous 60 days, and conversions fell about 25%. Check which campaigns and landing pages drove the change before you add budget.\n\n*Ask this follow-up: Which Google Ads campaigns and landing pages are driving the CPA increase?*",
  },
  {
    kind: "text",
    text: "**2. Test more budget on Bing Ads**\n\nBing Ads has your lowest CPA at $86, and it's held steady for 90 days. It gets only about 6% of your paid spend ($1,550 of $27,850 in the last 30 days). A small shift from Google Ads could add conversions at a lower cost.\n\n*Ask this follow-up: How many more conversions could I expect if I moved 10% of Google Ads budget to Bing Ads?*",
  },
  {
    kind: "text",
    text: "**3. Don't cut Meta Ads based on last touch**\n\nMeta Ads looks like your most expensive channel, with a $270 CPA under last touch. But Meta opens 64% of the conversion paths it appears in, and last touch doesn't credit that. Under first touch, Meta gets 79 conversions, for a CPA of about $92. Compare models before you make budget decisions for Meta.\n\n*Ask this follow-up: How does Meta Ads CPA compare across first-touch, linear and last-touch attribution?*",
  },
  VISUAL_7_CHART,
  {
    kind: "text",
    text: "**A note on data quality**\n\nAll three recommendation cards have a High data-quality rating this week. Spend and conversions are syncing for every paid channel. Google Ads is classified as a Closer and Meta Ads as an Opener, so the two channels do different jobs in the same journeys. Judge each one by the role it plays, not by last touch alone.\n\nData used: Appointment Request Submitted, last touch, Sep 13 to Oct 12, 2026, compared with Jul 15 to Sep 12.",
  },
  {
    kind: "text",
    text: "You can review and interact with all three recommendation cards directly: [View Recommendation Cards](/attribution)",
  },
];

const BODY_CONSENT: FakeBlock[] = [
  {
    kind: "text",
    text: "Yes, slightly. In October, 12.7% of events were withheld from your ad destinations (Meta Ads and Google Ads) because of visitor consent choices. That's up from 9.6% in April. The rise is gradual, about half a point a month, with the biggest step between July and August.",
  },
  VISUAL_8_KPIS,
  {
    kind: "text",
    text: "Your analytics is much less affected. Most visitors who opt out decline advertising but still allow analytics, so only 4.1% of events are excluded from your Freshpaint reporting.",
  },
  VISUAL_9_CHART,
  {
    kind: "text",
    text: "**What this means for your Meta and Google numbers:**\n\n- **The ad platforms see fewer conversions than you do.** Events from visitors who declined advertising aren't sent to Meta Ads or Google Ads. In October, that's roughly 1 in 8 events, so the platforms optimize on a smaller set of conversions than Freshpaint records.\n- **Your Freshpaint attribution is still close to complete.** With 4.1% excluded from analytics, the channel and conversion numbers in Freshpaint cover almost all of your site traffic.\n- **The banner is working as expected.** It's active and has recorded 52,400 visitor decisions in the last 30 days. You can review the setup at [Consent Management](/consent-management/overview).",
  },
  {
    kind: "text",
    text: "Data used: consent exclusions by destination, Apr 1 to Oct 12, 2026.",
  },
  {
    kind: "text",
    text: "*This is not legal advice, and AI can make mistakes, so please consult with your legal / compliance / privacy teams before making any decisions.*",
  },
];

const BODY_EXIT_PAGES: FakeBlock[] = [
  {
    kind: "text",
    text: "Here's where visitors left your site without converting over the last 30 days. Two metrics matter here. Non-converting exit volume is how many people leave from a page. Non-converting exit rate is the share of that page's views that end without a conversion, and it's the better signal of a \"leaky\" page.",
  },
  VISUAL_10_TABLE,
  { kind: "text", text: "**What to tackle**" },
  {
    kind: "text",
    text: "**/locations/northside-clinic/: 94.6% exit rate (dead-end page).** Almost every visitor who lands here leaves without converting. At 1,184 non-converting exits the volume isn't huge, but the rate points to a broken or high-friction experience. The cause could be a missing scheduling button, a broken form, or outdated content. The same pattern may show up on other individual location pages.\n\n*Ask this follow-up: Show me the non-converting exit rates for all individual location pages. Are other location pages also dead ends?*",
  },
  {
    kind: "text",
    text: "**/virtual-care/ and /find-a-doctor/: 78.5% and 55.6% exit rates.** Visitors come to both pages with a clear goal, either seeing a provider online or finding the right doctor, yet most leave without requesting an appointment. These pages likely need stronger calls to action or clearer next steps.\n\n*Ask this follow-up: Walk me through the full funnel for visitors who land on /virtual-care/. Where do they come from and where do they go?*",
  },
  {
    kind: "text",
    text: '**/locations/: 4,215 exits at a 45.0% rate.** The locations page is a high-traffic hub. People who arrive here are looking for a nearby office, but almost half leave without booking. A shorter path from locations to booking, like a "Schedule" button on each location, could add meaningful appointment volume.',
  },
  VISUAL_11_CHART,
  {
    kind: "text",
    text: "**A note on expected exits:** /pay-bill/confirmation is a natural end to a session (the screen after a payment), so its 60.0% exit rate isn't a concern. /pay-bill gets the most exits because existing patients pay and leave, not because prospective patients are dropping off. The homepage exiting at 47.2% is normal too. Focus on the high-rate pages where intent is clear but conversion is missing: the individual location page, virtual care and find a doctor.",
  },
  {
    kind: "text",
    text: "Data used: sessions and pageviews with Appointment Request Submitted as the conversion, Sep 13 to Oct 12, 2026.",
  },
];

const BODY_APPOINTMENTS_DROP: FakeBlock[] = [
  VISUAL_12_KPIS,
  {
    kind: "text",
    text: "Appointment requests are down 25% this week: 60 from Monday to Thursday, compared with 80 over the same days last week. Traffic held steady (5,940 sessions vs. 6,020), so the drop comes from fewer visitors converting, not fewer visitors. Your conversion rate fell from 1.33% to 1.01%.",
  },
  { kind: "text", text: "Three things stand out:" },
  { kind: "text", text: "**What to tackle**" },
  {
    kind: "text",
    text: "**Google Ads requests fell by almost half.** Google Ads went from 4.8 appointment requests a day to 2.5 (-47%), while its session volume stayed about the same. Visitors are still coming from Google Ads but converting less, which points to a landing page, keyword or ad change, not a budget cut. This matches the CPA increase in your recommendation cards.\n\n*Ask this follow-up: Break down Google Ads sessions and appointment requests by landing page this week vs. last week. Where are Google Ads visitors dropping off?*",
  },
  {
    kind: "text",
    text: "**Mobile is down much more than desktop.** Mobile appointment requests fell 35% (48 to 31), compared with 9% on desktop (32 to 29). That gap suggests a possible friction point on mobile, like form usability, load time or a recent layout change.\n\n*Ask this follow-up: Show me the appointment request funnel for mobile sessions this week vs. last week. Where does mobile drop off?*",
  },
  {
    kind: "text",
    text: "**/locations/northside-clinic/ stopped converting.** It went from 6 appointment requests last week to 0 this week, across 41 sessions. It's worth checking for a broken form, a missing scheduling button or a redirect issue.\n\n*Ask this follow-up: Pull session recordings of visitors who landed on /locations/northside-clinic/ this week and didn't convert. What are they doing instead?*",
  },
  {
    kind: "text",
    text: "**Good news: consent isn't driving this.** Analytics exclusions held at about 4% both weeks (4.0% last week, 4.1% this week), so the drop is real visitor behavior, not a data visibility issue.",
  },
  {
    kind: "text",
    text: "Data used: Appointment Request Submitted, Mon to Thu, Oct 5–8 vs. Oct 12–15, 2026.",
  },
];

// ---------------------------------------------------------------------------
// Explore-next suggestions, per the deck's "Explore Next" column. Even when two
// prompts share a body, their suggestions can differ, so each prompt gets its
// own list.
// ---------------------------------------------------------------------------

const SUGGESTIONS_TOP_PAID_CHANNEL = [
  "How do paid channels rank under first-touch or linear attribution?",
  "Is conversion data reaching Google Ads from this environment?",
  "What role does Google Ads play in conversion journeys?",
];

const SUGGESTIONS_PLATFORMS_MISMATCH = [
  "Which landing pages get the most direct-traffic conversions?",
  "Is conversion data reaching Google Ads and LinkedIn from this environment?",
  "What do the current recommendation cards say about channel mix?",
];

const SUGGESTIONS_GOOGLE_CONVERTING = [
  "How does Google Ads perform under a different attribution model, like linear or U-shaped?",
  "Which Google Ads campaigns or landing pages are driving the most conversions?",
  "Why is Meta Ads converting so much lower — what pages are Meta sessions landing on?",
];

const SUGGESTIONS_IMPROVE_PAID = [
  "How do I fix Meta Ads click ID pass-through in Freshpaint to improve attribution accuracy?",
  "Show me the Google Ads vs. Meta Ads conversion trend over the past 8 weeks",
  "Which landing pages are paid search sessions hitting, and how do they convert?",
];

const SUGGESTIONS_CONSENT = [
  "Which pages or devices have the highest opt-out rates?",
  "Which third-party trackers are active on the site and what's their compliance risk?",
  "Show me the consent configuration history to see what changed in August 2026",
];

const SUGGESTIONS_EXIT_PAGES = [
  "Show me the non-converting exit rates for all /locations/* pages",
  "What does the full booking funnel look like from /locations/ to appointment confirmation?",
  "Which channels are driving traffic to /virtual-care/ and how are those sessions converting?",
];

const SUGGESTIONS_APPOINTMENTS_DROP = [
  "Show me the Google Ads landing pages and their conversion rates this week vs. last week",
  "Pull session recordings of visitors who landed on /locations/northside-clinic/ this week but didn't convert",
  "Compare mobile vs. desktop conversion rates day-by-day over the last 14 days",
];

function withSuggestions(
  body: FakeBlock[],
  suggestions: string[],
): FakeBlock[] {
  return [...body, { kind: "suggestions", data: { suggestions } }];
}

const HCIC_RESPONSES_BY_PROMPT = new Map<string, FakeBlock[]>([
  [
    HCIC_PROMPTS.topPaidChannel,
    withSuggestions(BODY_TOP_PAID_CHANNEL, SUGGESTIONS_TOP_PAID_CHANNEL),
  ],
  [
    HCIC_PROMPTS.platformsMismatch,
    withSuggestions(BODY_TOP_PAID_CHANNEL, SUGGESTIONS_PLATFORMS_MISMATCH),
  ],
  [
    HCIC_PROMPTS.googleConverting,
    withSuggestions(BODY_GOOGLE_CONVERTING, SUGGESTIONS_GOOGLE_CONVERTING),
  ],
  [
    HCIC_PROMPTS.improvePaid,
    withSuggestions(BODY_IMPROVE_PAID, SUGGESTIONS_IMPROVE_PAID),
  ],
  [
    HCIC_PROMPTS.adEfficiency,
    withSuggestions(BODY_IMPROVE_PAID, SUGGESTIONS_IMPROVE_PAID),
  ],
  [HCIC_PROMPTS.optingOut, withSuggestions(BODY_CONSENT, SUGGESTIONS_CONSENT)],
  [
    HCIC_PROMPTS.consentIssue,
    withSuggestions(BODY_CONSENT, SUGGESTIONS_CONSENT),
  ],
  [
    HCIC_PROMPTS.exitPages,
    withSuggestions(BODY_EXIT_PAGES, SUGGESTIONS_EXIT_PAGES),
  ],
  [
    HCIC_PROMPTS.abandonPages,
    withSuggestions(BODY_EXIT_PAGES, SUGGESTIONS_EXIT_PAGES),
  ],
  [
    HCIC_PROMPTS.formFillsDrop,
    withSuggestions(BODY_APPOINTMENTS_DROP, SUGGESTIONS_APPOINTMENTS_DROP),
  ],
  [
    HCIC_PROMPTS.appointmentsDrop,
    withSuggestions(BODY_APPOINTMENTS_DROP, SUGGESTIONS_APPOINTMENTS_DROP),
  ],
]);

// Returns the canned HCIC response for a prompt, or the preview disclaimer +
// HCIC suggestions when the prompt isn't one of Kylie's. The HCIC env supports
// ONLY these prompts — it never falls through to the Meridian responses.
export function getHcicResponseForPrompt(prompt: string): FakeBlock[] {
  const matched = HCIC_RESPONSES_BY_PROMPT.get(prompt.trim());
  if (matched) return matched;
  return [
    { kind: "text", text: HCIC_PREVIEW_DISCLAIMER },
    { kind: "suggestions", data: { suggestions: HCIC_SUGGESTED_FOLLOWUPS } },
  ];
}

// One seeded sidebar thread per unique response body, so the booth demo opens
// onto a populated sidebar covering every topic.
export const HCIC_SEED_THREADS: Array<{ id: string; prompt: string }> = [
  { id: "preview-conv-hcic-top-paid", prompt: HCIC_PROMPTS.topPaidChannel },
  { id: "preview-conv-hcic-google", prompt: HCIC_PROMPTS.googleConverting },
  { id: "preview-conv-hcic-improve-paid", prompt: HCIC_PROMPTS.improvePaid },
  { id: "preview-conv-hcic-consent", prompt: HCIC_PROMPTS.optingOut },
  { id: "preview-conv-hcic-exit-pages", prompt: HCIC_PROMPTS.exitPages },
  { id: "preview-conv-hcic-appt-drop", prompt: HCIC_PROMPTS.formFillsDrop },
];
